import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { Printer, RefreshCw, Truck, PackageOpen, Tag } from "lucide-react";
import { API } from "@/config";

/* =============================================================================
   Daily Run Sheet — printable, driver-focused view of every delivery + pickup
   for the selected date. Rendered on-screen with a "Print" button that calls
   window.print(); the print CSS strips the shell chrome so the driver ends up
   with a clean, one-page-per-day PDF.

   Backend: GET /api/run-sheet?day=YYYY-MM-DD  (admin OR employee access).
============================================================================= */
export const RunSheet = ({ containerClass = "space-y-6 print:space-y-3" }) => {
  const [params] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);
  const [day, setDay] = useState(() => params.get("day") || today);
  const [data, setData] = useState({ date: today, deliveries: [], pickups: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/run-sheet`, { params: { day } });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load the run sheet.");
    } finally { setLoading(false); }
  }, [day]);
  useEffect(() => { load(); }, [load]);

  const totalStops = (data.deliveries?.length || 0) + (data.pickups?.length || 0);

  return (
    <div className={containerClass} data-testid="run-sheet">
      {/* Toolbar — hidden when printing */}
      <div className="flex items-center gap-3 flex-wrap print:hidden">
        <input
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="rounded-lg bg-black/30 border border-[#FFD166]/25 px-3 py-2 text-sm text-[#F6F1E4]"
          data-testid="run-sheet-date"
        />
        <button onClick={load} disabled={loading} className="btn-ghost inline-flex items-center gap-2 disabled:opacity-60" data-testid="run-sheet-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
        <Link
          to={`/admin/labels?day=${day}&autoprint=1`}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost inline-flex items-center gap-2"
          data-testid="run-sheet-print-tags"
          title="Opens the Customer Tags page for this day and fires the print dialog automatically"
        >
          <Tag className="w-3.5 h-3.5" />
          Print today's tags
        </Link>
        <button onClick={() => window.print()} className="btn-primary inline-flex items-center gap-2 ml-auto" data-testid="run-sheet-print">
          <Printer className="w-3.5 h-3.5" />
          Print / Save PDF
        </button>
      </div>

      {/* Print header — only visible when printing */}
      <div className="hidden print:block text-black">
        <h1 className="text-2xl font-serif">Enchanted Gear Rentals — Daily Run Sheet</h1>
        <p className="text-sm mt-1">{data.date} · {data.deliveries?.length || 0} deliveries · {data.pickups?.length || 0} pickups</p>
        <hr className="my-3 border-black/30" />
      </div>

      <div className="print:hidden">
        <h1 className="font-serif text-3xl text-[#FFD166]">Daily Run Sheet</h1>
        <p className="text-sm text-[#B9C8D5]">{data.date} · {totalStops} stop{totalStops === 1 ? "" : "s"}</p>
      </div>

      <Section title="Deliveries" icon={Truck} items={data.deliveries || []} kind="delivery" />
      <Section title="Pickups"    icon={PackageOpen} items={data.pickups || []} kind="pickup" />

      {totalStops === 0 && !loading && (
        <div className="rounded-xl border border-[#FFD166]/15 bg-black/30 p-6 text-center text-[#B9C8D5] print:hidden" data-testid="run-sheet-empty">
          Nothing scheduled for {data.date}. Take the day off ✨
        </div>
      )}
    </div>
  );
};

const Section = ({ title, icon: Icon, items, kind }) => (
  <section className="space-y-3 print:space-y-1" data-testid={`run-sheet-${kind}-section`}>
    <div className="flex items-center gap-2 print:text-black">
      <Icon className="w-4 h-4 text-[#FFD166] print:text-black" />
      <h2 className="font-serif text-xl text-[#FFD166] print:text-black">
        {title} <span className="text-[#B9C8D5] print:text-black text-xs">({items.length})</span>
      </h2>
    </div>
    {items.length === 0 ? (
      <p className="text-xs text-[#B9C8D5]/60 italic print:text-black print:italic">No {title.toLowerCase()} today.</p>
    ) : (
      <div className="space-y-3 print:space-y-1">
        {items.map((it) => (
          <div
            key={`${kind}-${it.reservation_id}`}
            className="rounded-xl border border-[#FFD166]/20 bg-black/30 p-4 print:border print:border-black/50 print:bg-white print:text-black print:break-inside-avoid"
            data-testid={`run-sheet-row-${kind}-${it.reservation_id}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-serif text-lg text-[#F6F1E4] print:text-black">{it.customer_name}</div>
                {it.customer_phone && <a href={`tel:${it.customer_phone}`} className="text-sm text-[#FFD166] print:text-black">{it.customer_phone}</a>}
              </div>
              <span className="text-xs uppercase tracking-[0.2em] rounded-full border border-[#FFD166]/40 px-3 py-1 text-[#FFD166] print:border-black print:text-black">
                {it.slot}
              </span>
            </div>
            <div className="text-sm text-[#F6F1E4] mt-2 print:text-black">
              📍 <b>{it.address}</b>
            </div>
            {(it.line_items || []).length > 0 && (
              <ul className="mt-3 text-sm text-[#B9C8D5] print:text-black grid grid-cols-1 sm:grid-cols-2 gap-y-1">
                {it.line_items.map((li, i) => (
                  <li key={li.product_id}>• {li.quantity}× {li.name}</li>
                ))}
              </ul>
            )}
            {it.notes && (
              <div className="mt-3 text-xs text-[#F4A93A] print:text-black italic">Notes: {it.notes}</div>
            )}
            {it.damage_protection && (
              <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-emerald-300 print:text-black">
                ✓ Damage Protection Plan
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </section>
);
